import { useCallback, useSyncExternalStore } from 'react';
import type { DirectoryGridColumns } from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';

/**
 * Desktop column counts offered by the browse pages' "Display: {n}" dropdown.
 * Mobile column counts are fixed per vertical and unaffected by the choice.
 */
export const DIRECTORY_DISPLAY_COLUMN_OPTIONS: readonly DirectoryGridColumns[] =
    [2, 3, 4, 5, 6];

export const isDirectoryGridColumns = (
    value: number,
): value is DirectoryGridColumns =>
    DIRECTORY_DISPLAY_COLUMN_OPTIONS.some((option) => option === value);

/**
 * Desktop half of `DirectoryUniformGrid`'s column classes, for grids that set
 * their own mobile column count. 5 and 6 only apply from lg — between md and lg
 * there is not enough width, so they hold at 4. Kept in step with `COL_CLASSES`
 * in `category-page/DirectoryUniformGrid.tsx`.
 */
export const DIRECTORY_DESKTOP_COL_CLASSES: Record<
    DirectoryGridColumns,
    string
> = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    5: 'md:grid-cols-4 lg:grid-cols-5',
    6: 'md:grid-cols-4 lg:grid-cols-6',
};

// The chosen column count lives in an in-memory map (source of truth for the
// current document) mirrored to sessionStorage, keyed by entity type, so it
// survives both SPA navigation (into a detail page and back) and a full page
// refresh. Keeping the count stable on Back also keeps the grid's page height
// deterministic, which is what makes its scroll-position restore land
// correctly. When sessionStorage is unavailable (private mode, blocked
// cookies) the in-memory value still applies until the next full page load.
const STORAGE_KEY_PREFIX = 'directory-display-columns:';

const storageKey = (key: string): string => `${STORAGE_KEY_PREFIX}${key}`;

const memoryColumns = new Map<string, DirectoryGridColumns>();

const listeners = new Map<string, Set<() => void>>();

const readColumns = (
    key: string,
    fallback: DirectoryGridColumns,
): DirectoryGridColumns => {
    if (typeof window === 'undefined') return fallback;
    const inMemory = memoryColumns.get(key);
    if (inMemory !== undefined) return inMemory;
    try {
        const stored = Number(window.sessionStorage.getItem(storageKey(key)));
        return isDirectoryGridColumns(stored) ? stored : fallback;
    } catch {
        // sessionStorage can throw (private mode, blocked cookies) — use the default.
        return fallback;
    }
};

const writeColumns = (key: string, columns: DirectoryGridColumns): void => {
    memoryColumns.set(key, columns);
    if (typeof window !== 'undefined') {
        try {
            window.sessionStorage.setItem(storageKey(key), String(columns));
        } catch {
            // Best-effort persistence only — the in-memory value above keeps
            // the control working for the rest of this document's life.
        }
    }
    listeners.get(key)?.forEach((notify) => notify());
};

const subscribe = (key: string, notify: () => void): (() => void) => {
    const set = listeners.get(key) ?? new Set<() => void>();
    set.add(notify);
    listeners.set(key, set);
    return () => set.delete(notify);
};

/**
 * Read + set the persisted Display column count for a browse entity.
 *
 * Backed by useSyncExternalStore so the value is read *synchronously* on a
 * client remount — hitting Back from a detail page restores the chosen column
 * count on the very first render, so the grid's scroll restore lands at the
 * right layout. During SSR and the one-time hydration it reports the entity's
 * default (the server snapshot), avoiding a hydration mismatch; the stored
 * value is then applied on the first post-hydration commit.
 */
export const useDirectoryDisplayColumns = (
    key: string,
    defaultColumns: DirectoryGridColumns,
): {
    columns: DirectoryGridColumns;
    setColumns: (columns: DirectoryGridColumns) => void;
} => {
    // Both callbacks are memoised: a fresh subscribe identity would make
    // React tear down and re-create the store subscription on every render
    // of the (large) route components that call this hook.
    const subscribeToKey = useCallback(
        (notify: () => void) => subscribe(key, notify),
        [key],
    );
    const getSnapshot = useCallback(
        () => readColumns(key, defaultColumns),
        [key, defaultColumns],
    );
    const getServerSnapshot = useCallback(
        () => defaultColumns,
        [defaultColumns],
    );
    const columns = useSyncExternalStore(
        subscribeToKey,
        getSnapshot,
        getServerSnapshot,
    );
    const setColumns = useCallback(
        (next: DirectoryGridColumns) => writeColumns(key, next),
        [key],
    );
    return { columns, setColumns };
};
